import RAPIER from '@dimforge/rapier3d-compat'

import { buildTerrainGeometry, isTerrainHole } from '../terrainGeometry'
import { buildTerrainIsosurface } from '../terrainIsosurface'
import type {
  RobloxMaterial,
  SceneDescriptor,
  TerrainDescriptor,
} from '../types'
import { physicalPropertiesFor } from './materialPhysicalProperties'

/** 穴を除くセルの最多材質。同数なら小さいパレット番号、セルが無ければ Grass。 */
export const dominantTerrainMaterial = (
  terrain: TerrainDescriptor,
): RobloxMaterial => {
  const counts = terrain.palette.map(() => 0)
  for (let index = 0; index < terrain.heights.length; index += 1) {
    if (!isTerrainHole(terrain.heights[index])) {
      const materialIndex = terrain.materials[index]
      if (
        !Number.isInteger(materialIndex) ||
        counts[materialIndex] === undefined
      ) {
        throw new Error(
          `地形セルの材質インデックスが不正です: ${materialIndex}`,
        )
      }
      counts[materialIndex] += 1
    }
  }
  let dominantIndex = -1
  let largestCount = 0
  for (let index = 0; index < counts.length; index += 1) {
    if (counts[index] > largestCount) {
      dominantIndex = index
      largestCount = counts[index]
    }
  }
  return dominantIndex === -1 ? 'Grass' : (
      terrain.palette[dominantIndex].material
    )
}

/** 背景（terrain・焼き込み静的メッシュ）の trimesh コライダーを物理 World へ生成する。 */
export const createStaticSceneColliders = (
  physicsWorld: RAPIER.World,
  descriptor: SceneDescriptor,
): void => {
  // Terrain の trimesh コライダー。描画（terrainView）と同じ三角形から作り、
  // 見た目どおりの曲面を歩ける・登れるようにする。ボクセルがあれば等値面
  // （オーバーハング・洞窟まで一致）、無ければ旧形式の高さマップ。Blockly の
  // 操作対象ではないため objectBodies へは登録せず、Touched も発火しない（背景扱い）。
  if (descriptor.terrain) {
    const terrainGeometry =
      buildTerrainIsosurface(descriptor.terrain) ??
      buildTerrainGeometry(descriptor.terrain)
    if (terrainGeometry) {
      const props = physicalPropertiesFor(
        dominantTerrainMaterial(descriptor.terrain),
      )
      const terrainBody = physicsWorld.createRigidBody(
        RAPIER.RigidBodyDesc.fixed(),
      )
      physicsWorld.createCollider(
        RAPIER.ColliderDesc.trimesh(
          terrainGeometry.positions,
          terrainGeometry.indices,
        )
          .setFriction(props.friction)
          .setRestitution(props.elasticity)
          .setFrictionCombineRule(RAPIER.CoefficientCombineRule.Average)
          .setRestitutionCombineRule(RAPIER.CoefficientCombineRule.Average),
        terrainBody,
      )
    }
  }

  // 焼き込み静的メッシュ（Union の実形状など）の trimesh コライダー。
  // 描画（staticMeshView）と同じ三角形を使い、本家どおりの形で歩ける。
  // terrain 同様に背景扱い（objectBodies 非登録・Touched なし）。
  for (const staticMesh of descriptor.staticMeshes ?? []) {
    const props = physicalPropertiesFor(staticMesh.material ?? 'Plastic')
    const body = physicsWorld.createRigidBody(RAPIER.RigidBodyDesc.fixed())
    physicsWorld.createCollider(
      RAPIER.ColliderDesc.trimesh(
        new Float32Array(staticMesh.positions),
        new Uint32Array(staticMesh.indices),
      )
        .setFriction(props.friction)
        .setRestitution(props.elasticity)
        .setFrictionCombineRule(RAPIER.CoefficientCombineRule.Average)
        .setRestitutionCombineRule(RAPIER.CoefficientCombineRule.Average),
      body,
    )
  }
}

// 落下リスポーン閾値（GAP-FALLOUT）。シーン最下面からさらに下に外れたら
// 奈落へ落ちたとみなし spawn へ戻す（Roblox の FallenPartsDestroyHeight 相当の
// 汎用挙動）。スクリプト駆動の落下ハザードも、最終的にこの汎用復帰で吸収する。
export const computeFallThreshold = (
  descriptor: SceneDescriptor,
  spawnY: number,
): number => {
  const FALL_RESPAWN_MARGIN = 50
  let sceneMinY = spawnY
  for (const object of descriptor.objects) {
    sceneMinY = Math.min(sceneMinY, object.position.y - object.size.y / 2)
  }
  // Terrain（谷・海底）はオブジェクトより低い地表になり得る。その上を歩いて
  // いるだけで奈落扱いにならないよう、有効セルの最低高も最下面に含める。
  if (descriptor.terrain) {
    for (const height of descriptor.terrain.heights) {
      if (!isTerrainHole(height)) {
        sceneMinY = Math.min(sceneMinY, height)
      }
    }
  }
  // 静的メッシュ（Union 等）の最下面も同様に含める。
  for (const staticMesh of descriptor.staticMeshes ?? []) {
    for (let i = 1; i < staticMesh.positions.length; i += 3) {
      sceneMinY = Math.min(sceneMinY, staticMesh.positions[i])
    }
  }
  return sceneMinY - FALL_RESPAWN_MARGIN
}
